import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  type DataObjectGroupsWithDescriptionsMapById,
  DataObjectGroupType,
  keepStaleInMinutes,
  useDataObjectGroupsWithDescriptionsQuery,
  useQueryParams,
} from '@atlas-frontend/sdk';
import type { ViewType } from './SampleContext.types';

type SampleDetailsContextValue = {
  viewType: ViewType;
  setViewType: (val: ViewType) => void;
  treeData?: DataObjectGroupsWithDescriptionsMapById;
  isEditing: boolean;
};

const SampleDetailsContext = createContext<
  SampleDetailsContextValue | undefined
>(undefined);

export const useSampleDetailsContext = () => {
  const context = useContext(SampleDetailsContext);

  if (context === undefined) {
    throw new Error(
      'useSampleDetailsContext must be used within a SampleDetailsContextProvider',
    );
  }

  return context;
};

export const SampleDetailsContextProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [{ viewType = 'detail' }, setQueryParams] = useQueryParams<{
    viewType: ViewType;
  }>();
  const setViewType = useCallback(
    (vt: ViewType, success?: boolean) => {
      setQueryParams(
        { viewType: vt },
        success ? { state: { saved: 'success' } } : undefined,
      );
    },
    [setQueryParams],
  );

  const isEditing = viewType === 'mutate';

  const { data: subjectTreeData } = useDataObjectGroupsWithDescriptionsQuery(
    { groupType: DataObjectGroupType.I },
    { placeholderData: keepPreviousData, ...keepStaleInMinutes(1) },
  );

  const value = useMemo<SampleDetailsContextValue>(
    () => ({
      viewType,
      setViewType,
      isEditing,
      treeData: subjectTreeData,
    }),
    [isEditing, viewType, setViewType, subjectTreeData],
  );

  return (
    <SampleDetailsContext.Provider value={value}>
      {children}
    </SampleDetailsContext.Provider>
  );
};
